import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import { Prices } from './Tc.Module.LegoContainer';
import { OpenOverlayData } from '../Tc.Module.OrderOverlay';
import type { SiebelProduct } from '../Tc.Module.ServiceAvailability';
import { OrderButtonsInitData } from '../Tc.Module.OrderOverlayOrderButtons';

const MAP_SLIDER_ID_TO_ORDER_LINK_PARAM = {
	internet: 'Internet',
	service: 'Service',
	tv: 'TV',
	voip: 'Voip',
};

type SelectionTypeKey = keyof typeof MAP_SLIDER_ID_TO_ORDER_LINK_PARAM | 'mobile';

interface Product {
	siebelId : string;
	priceDifference : number;
}

interface LegoConfiguration {
	currentSelection : Record<SelectionTypeKey, {
		cost : string | number;
		slideId : string;
	}>;
	deeplinkHashParameters : string;
	prices : Prices;
	productIds : Product[];
}

export interface FlavourAvailability {
	productAvailability : {
		siebelId : string;
		availability : 'available';
	}[];
}

function formattedPrice(amount : number, perMonth : string) {
	return `CHF&nbsp;${SCS.utils.formatPriceWithDash(amount)}${perMonth}`;
}

/**
 * Lego Order Button.
 *
 * @see {@link https://wiki.swisscom.com/pages/viewpage.action?pageId=169925848} for order link parameter details.
 * @example
 *        https://<host>:<port>/map/online/qualifyOrder.html
 *        ?flavourSelection=Internet%3AInternet+Flavour+LEGO+Home+L%3BTV%3ALEGO+No+TV%3BVoip%3ALego+No+VoIP
 *        &siebelProductId=LEGO+Home
 *        &lang=de
 *        &returnUrl=http%3A%2F%2Fwww.swisscom.com%2F
 *        &cancelUrl=http%3A%2F%2Fwww.swisscom.com%2F
 *
 * @author Cesar Keller
 * @namespace Tc.Module
 * @class LegoOrderButton
 * @extends Tc.Module
 */
export default class LegoOrderButton extends Module {
	private orderLinkExistingCustomer! : string;
	private orderLinkNewCustomer! : string;
	private orderLinkExistingCustomerMobile! : string;
	private orderLinkNewCustomerMobile! : string;
	private $existingCustomerButton! : JQuery;
	private siebelProductId! : string;

	private selectedProductId? : SiebelProduct;
	private productIds? : Product[];
	private deeplinkHashParameters? : string;
	private prices? : Prices;
	private flavourSelection? : string;

	private additionalParams! : Record<string, string>;

	public on(callback : Hook) {
		const $ctx = this.$ctx;

		this.orderLinkExistingCustomer = $ctx.data('order-link-existing-customer');
		this.orderLinkNewCustomer = $ctx.data('order-link-new-customer');

		this.orderLinkExistingCustomerMobile = $ctx.data('order-link-existing-customer-mobile');
		this.orderLinkNewCustomerMobile = $ctx.data('order-link-new-customer-mobile');

		this.$existingCustomerButton = $('.modOrderOverlay').eq(0).find('.existing-customer');

		this.siebelProductId = $ctx.data('siebel-product-id');

		this.sandbox.subscribe('LEGO_CONTAINER', this);
		this.$ctx.find('.lego-order-button').on('click', this.openOverlay.bind(this));

		this.additionalParams = this.$ctx.data('additionalParams') || {};

		callback();
	}

	/**
	 * Open the order overlay.
	 * Update the mobile buttons
	 *
	 * @fires	'openOverlay'
	 */
	public openOverlay(event : JQueryMouseEventObject) {
		// If this button is in a form or multi-step form, prevent
		// the default submit action
		event.preventDefault();

		const data : OpenOverlayData = {
			orderButtonsId: 'lego-order-buttons-mobile',
			orderLink: this.addParamsExistingCustomer(this.orderLinkExistingCustomer),
			orderLinkNoLogin: this.addParamsNewCustomer(this.orderLinkNewCustomer),
			productIds: this.selectedProductId,
			productName: '',
		};

		this.fire('openOverlay', data, ['orderoverlaystatus']);

		this.updateMobileButtons();
		this.$existingCustomerButton.on('click', this.handleExistingCustomer.bind(this));
	}

	/**
	 * Show 'order steps' dialog before linking to MAP if order step is configured.
	 */
	public handleExistingCustomer(event : JQueryMouseEventObject) : boolean {
		const $ctx = this.$ctx;
		const orderStepsContent = $ctx.data('order-steps-content');

		// No order step configured
		// or the button is a button in the order step dialog. In this case just
		// go to the url in the href of the anchor tag.
		if (!orderStepsContent || $(event.target).hasClass('goToLogin')) {
			return true;
		}

		// Order step configured, we need to load and display first.
		$.ajax({
			success: data => {
				const $services = $ctx.closest('.modLegoContainer').find('.summary .services');
				const perMonth = $ctx.data('per-month');
				const orderButtonText = $ctx.data('order-button-text');

				let $home = $services.clone().css('display', 'table-cell');
				$home.find('.mobile').addClass('hidden');
				$home.find('.internet').css('margin-left', '0');
				$home = $('<div/>').addClass('modLegoContainer').append($home);

				let $mobile = $services.clone().css('display', 'table-cell');
				$mobile.find('.internet, .service, .tv, .voip').addClass('hidden');
				$mobile.find('.mobile').css('margin-left', '0');
				$mobile = $('<div/>').addClass('modLegoContainer').append($mobile);

				// Replace placeholders for prices, and selected flavours
				data = data
					.replace(/\$\{bundleBasePrice\}/g, formattedPrice(this.prices?.bundleBasePrice ?? 0, perMonth))
					.replace(/\$\{homePrice\}/g, formattedPrice(this.prices?.homePrice ?? 0, perMonth))
					.replace(/\$\{mobilePrice\}/g, formattedPrice(this.prices?.mobilePrice ?? 0, perMonth))
					.replace(/\$\{totalPrice\}/g, formattedPrice(this.prices?.totalPrice ?? 0, perMonth))
					.replace(/\$\{home\}/g, $home[0].outerHTML)
					.replace(/\$\{mobile\}/g, $mobile[0].outerHTML);

				const $orderButton = $('<div/>')
					.addClass('button-group button-group--right button-group--responsive')
					.append(this.$existingCustomerButton.clone(true));
				$orderButton.find('.button').text(orderButtonText).addClass('goToLogin');

				$('<div/>').append(data).append($orderButton).dialog({
					classes: {'ui-dialog': 'order-overlay-dialog sdx-container'},
					width: 780,
				});
			},
			url: orderStepsContent + '/_jcr_content/par.html',
		});

		return false;
	}

	// Update mobile buttons in this module and the clone in the order overlay
	public updateMobileButtons() {
		let existingCustomerUrl = this.orderLinkExistingCustomerMobile;
		let newCustomerUrl = this.addParamsNewCustomer(this.orderLinkNewCustomerMobile);
		const $createLoginButton = $('.modOrderOverlay .new-customer .button[href]');

		if (!existingCustomerUrl) {
			existingCustomerUrl = this.addParamsExistingCustomer(this.orderLinkExistingCustomer);
		} else if (existingCustomerUrl && !String(existingCustomerUrl).trim().startsWith('tel:')) {
			existingCustomerUrl = this.addParamsExistingCustomer(existingCustomerUrl);
		}

		// Handle BBI: if swisscomLoginRequired is enabled in Order Overlay, use that button's href and label
		if ($createLoginButton.length) {
			newCustomerUrl = $createLoginButton.prop('href');

			const newCustomerUrlLabel = $createLoginButton.text();
			this.$ctx.find('.order-link-new-customer-mobile .button').text(newCustomerUrlLabel);
			$('#orderoverlay').find('.order-link-new-customer-mobile .button').text(newCustomerUrlLabel);
		}

		this.$ctx.find('.order-link-existing-customer-mobile').attr('href', existingCustomerUrl);
		$('#orderoverlay').find('.order-link-existing-customer-mobile').attr('href', existingCustomerUrl);

		this.$ctx.find('.order-link-new-customer-mobile').attr('href', newCustomerUrl);
		$('#orderoverlay').find('.order-link-new-customer-mobile').attr('href', newCustomerUrl);
	}

	public onDialogClosed() {
		this.$existingCustomerButton.off('click');
	}

	// Fired by Tc.Module.ServiceAvailability
	public onServiceAvailabilityDataUpdate() {
		const mod = this;

		// The user has already entered his phone number or address,
		// so this is a good time to check all the flavours at once
		mod.fire('checkProductIds', {
			productIds: this.productIds,
			success: (data : FlavourAvailability) => {
				// Send results to Lego Configurator
				this.fire('flavourAvailability', data, ['LEGO_CONTAINER']);
			},
		});
	}

	/**
	 * Terrific Handler, fired by Tc.Module.LegoContainer.Configurator.
	 * Collects all selected product IDs and prepares the parameters needed for the order button URLs.
	 */
	public onNewLegoConfiguration(data : LegoConfiguration) {
		// I18n
		const yourSelection = this.$ctx.data('your-selection');
		const currentSelection = data && data.currentSelection;
		const deeplinkHashParameters = data && data.deeplinkHashParameters;
		const prices = data && data.prices;
		// All flavours
		const productIds = data && data.productIds;

		const flavourSelection = (Object.keys(currentSelection) as SelectionTypeKey[])
			.filter(key => key in MAP_SLIDER_ID_TO_ORDER_LINK_PARAM)
			.map(key => `${MAP_SLIDER_ID_TO_ORDER_LINK_PARAM[key as keyof typeof MAP_SLIDER_ID_TO_ORDER_LINK_PARAM]}:${currentSelection[key].slideId}`)
			.join(';');

		if(currentSelection.internet) {
			this.selectedProductId = {name: yourSelection, siebelId: currentSelection.internet.slideId};
		}

		// Save for later
		this.flavourSelection = flavourSelection;
		this.deeplinkHashParameters = deeplinkHashParameters;
		this.productIds = productIds;
		this.prices = prices;
		this.log('onNewLegoConfiguration', flavourSelection, this.selectedProductId, productIds, prices);

		// Update buttons for desktop and mobile (in case the overlay is already open)
		const orderButtonsInitData : OrderButtonsInitData = {
			href: this.addParamsExistingCustomer(this.orderLinkExistingCustomer),
			noLoginUrl: `${String(this.orderLinkNewCustomer)}#lpIds=${this.deeplinkHashParameters}`,
			update: true, // Do not init/reset the button state, only update the URLs
		};
		this.fire('orderButtonsInit', orderButtonsInitData, ['orderoverlaystatus']);

		this.updateMobileButtons();
		this.fire('updateProductId', this.selectedProductId, ['serviceavailabilitycheck']);
	}

	private addParamsNewCustomer(url : string) {
		let result = `${String(url)}#lpIds=${String(this.deeplinkHashParameters)}`;

		for(const additionalParam in this.additionalParams) {
			result += `&${additionalParam}=${encodeURIComponent(this.additionalParams[additionalParam])}`;
		}

		return result;
	}

	public addParamsExistingCustomer(url : string) {
		const language = this.$ctx.data('language') as string;

		const params = [
			'flavourSelection', this.flavourSelection!,
			'siebelProductId', this.siebelProductId,
			'lang', language,
			'returnUrl', location.href,
			'cancelUrl', location.href,
		];

		for(const additionalParam in this.additionalParams) {
			params.push(additionalParam, this.additionalParams[additionalParam]);
		}

		let result = url + '?';

		for(let i = 0; i < params.length; i += 2) {
			result += `${i === 0 ? '' : '&'}${params[i]}=${encodeURIComponent(params[i + 1])}`;
		}

		return result;
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/Tc.Module.LegoOrderButton.ts